/**
 * Get Product Recommendations Action
 * Triggered by: arc:action:getRecommendations
 *
 * Delegates to the active search provider's getRecommendations() method.
 *
 * event.detail:
 *   blockId      {string}        - Unique block ID for event namespacing
 *   productId    {string|number} - Seed product Shopify numeric ID
 *   intent       {string}        - Shopify: 'RELATED' | 'COMPLEMENTARY'
 *   tags         {string}        - SearchSpring: comma-separated profile tag(s)
 *   limit        {number}        - Max products to return
 *   cartItemIds  {string}        - Comma-separated numeric product IDs in cart
 */

import { createProvider } from '@js/providers/providerFactory.js';

const waitForPostLoadIdle = (timeout = 2500) => {
  const safeTimeout = Number.isFinite(timeout) && timeout >= 0 ? timeout : 2500;

  return new Promise((resolve) => {
    const queueAtIdle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: safeTimeout });
      } else {
        setTimeout(resolve, Math.min(safeTimeout, 1000));
      }
    };

    if (document.readyState === 'complete') {
      queueAtIdle();
    } else {
      window.addEventListener('load', queueAtIdle, { once: true });
    }
  });
};

export const runGetRecommendations = async (data = {}) => {
  const {
    blockId,
    productId,
    intent,
    tags,
    limit = 10,
    cartItemIds,
    defer = false,
    deferTimeout = 2500
  } = data;

  try {
    if (defer) {
      await waitForPostLoadIdle(deferTimeout);
    }

    const provider = await createProvider(window.search?.provider || 'shopify');
    const products = await provider.getRecommendations({ productId, intent, tags, limit, cartItemIds });

    const eventName = blockId ? `recommendations:fetched:${blockId}` : 'recommendations:fetched';
    window.dispatchEvent(new CustomEvent(eventName, { detail: { products } }));
    
    return { success: true, products };
  } catch (error) {
    console.error('[Recommendations] Error fetching recommendations:', error);

    const eventName = blockId ? `recommendations:error:${blockId}` : 'recommendations:error';
    window.dispatchEvent(new CustomEvent(eventName, { detail: { error: error.message } }));

    return { success: false, error: error.message };
  }
};

